import { StateCreator } from 'zustand'
import {
  BaseQuestionnaireResponseFragment,
  BaseQuestionnaireWithResponseListFragment,
  Get_Latest_Questionnaire_ResponseQuery
} from 'generated/graphql'
import { getLatestResponseFromQuestionnaire } from 'components/Assessments/hooks/helpers'
import { RootSlice } from './rootType'

interface LoadPreviousAssessmentOptions {
  currentAssessmentId?: string
  shouldFetchUnfinished?: boolean
}

export interface AssessmentSlice {
  assessment: {
    pat: {
      list: BaseQuestionnaireResponseFragment[]
      previous: BaseQuestionnaireResponseFragment | null
    }
    soc: {
      previous: BaseQuestionnaireResponseFragment | null
    }
    recert: {
      previous: BaseQuestionnaireResponseFragment | null
    }
    fua: {
      previous: BaseQuestionnaireResponseFragment | null
    }
    roc: {
      previous: BaseQuestionnaireResponseFragment | null
    }
    resetState: () => void
    savePATList: (data: Get_Latest_Questionnaire_ResponseQuery) => void
    loadPreviousSOC: (
      questionnaireResponse: Get_Latest_Questionnaire_ResponseQuery,
      options: LoadPreviousAssessmentOptions
    ) => void
    loadPreviousRecert: (
      questionnaireResponse: Get_Latest_Questionnaire_ResponseQuery,
      options: LoadPreviousAssessmentOptions
    ) => void
    loadPreviousFua: (
      questionnaireResponse: Get_Latest_Questionnaire_ResponseQuery,
      options: LoadPreviousAssessmentOptions
    ) => void
    loadPreviousROC: (
      questionnaireResponse: Get_Latest_Questionnaire_ResponseQuery,
      options: LoadPreviousAssessmentOptions
    ) => void
  }
}

// TODO (Jason): check
export const createAssessmentSlice: StateCreator<
  RootSlice,
  // Type `never` is used here to follow the instructions on Zustand docs
  // reference: https://github.com/pmndrs/zustand/blob/main/docs/guides/typescript.md#middlewares-and-their-mutators-reference
  // see index.ts for middlewares being used
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AssessmentSlice
> = (set) => ({
  assessment: {
    pat: {
      list: [],
      previous: null
    },
    soc: {
      previous: null
    },
    recert: {
      previous: null
    },
    fua: {
      previous: null
    },
    roc: {
      previous: null
    },
    resetState: () => {
      set((state) => {
        state.assessment.pat = {
          list: [],
          previous: null
        }
        state.assessment.soc = {
          previous: null
        }
        state.assessment.recert = {
          previous: null
        }
        state.assessment.fua = {
          previous: null
        }
        state.assessment.roc = {
          previous: null
        }
      })
    },
    savePATList: async (data: Get_Latest_Questionnaire_ResponseQuery) => {
      const allAssessments =
        data.QuestionnaireList?.flatMap(
          (questionnaire) =>
            questionnaire?.QuestionnaireResponseList?.filter(
              (questionnaireResponse): questionnaireResponse is BaseQuestionnaireResponseFragment =>
                !!questionnaireResponse
            ) ?? []
        ) ?? []

      set((state) => {
        state.assessment.pat.list = allAssessments
      })
    },
    loadPreviousSOC: async (questionnaireResponse, options) => {
      set((state) => {
        state.assessment.soc.previous = null
      })

      const { currentAssessmentId } = options ?? {}

      const questionnaireList =
        questionnaireResponse.QuestionnaireList?.filter(
          (q): q is BaseQuestionnaireWithResponseListFragment => !!q
        ) ?? []

      const latestResponse = getLatestResponseFromQuestionnaire(questionnaireList, {
        currentAssessmentId
      })

      set((state) => {
        state.assessment.soc.previous = latestResponse
      })
    },
    loadPreviousRecert: async (questionnaireResponse, options) => {
      set((state) => {
        state.assessment.recert.previous = null
      })

      const { currentAssessmentId } = options ?? {}

      const questionnaireList =
        questionnaireResponse.QuestionnaireList?.filter(
          (q): q is BaseQuestionnaireWithResponseListFragment => !!q
        ) ?? []

      const latestResponse = getLatestResponseFromQuestionnaire(questionnaireList, {
        currentAssessmentId
      })

      set((state) => {
        state.assessment.recert.previous = latestResponse
      })
    },
    loadPreviousFua: async (questionnaireResponse, options) => {
      set((state) => {
        state.assessment.fua.previous = null
      })

      const { currentAssessmentId } = options ?? {}

      const questionnaireList =
        questionnaireResponse.QuestionnaireList?.filter(
          (q): q is BaseQuestionnaireWithResponseListFragment => !!q
        ) ?? []

      const latestResponse = getLatestResponseFromQuestionnaire(questionnaireList, {
        currentAssessmentId
      })

      set((state) => {
        state.assessment.fua.previous = latestResponse
      })
    },
    loadPreviousROC: async (questionnaireResponse, options) => {
      set((state) => {
        state.assessment.roc.previous = null
      })

      const { currentAssessmentId } = options ?? {}

      const questionnaireList =
        questionnaireResponse.QuestionnaireList?.filter(
          (q): q is BaseQuestionnaireWithResponseListFragment => !!q
        ) ?? []

      const latestResponse = getLatestResponseFromQuestionnaire(questionnaireList, {
        currentAssessmentId
      })

      set((state) => {
        state.assessment.roc.previous = latestResponse
      })
    }
  }
})
